import { action, observable } from "mobx";
import { Role } from "../constants";
import { call } from "lib/apiHelper";
import { UserTitleRolesApi as Api, NotExistUserTitleRole } from "lib/Api";
import { defer } from "lib/defer";
import { notificationStore } from "lib/store";

export type InitialProps = {
  titleUuid: string;
};

let store: Store | undefined;

export function getStore() {
  if (!store) {
    throw new Error("Store not initialized");
  }
  return store;
}

export function createStore(props: InitialProps) {
  store = new Store(props);
}

export type UserTitleRole = NotExistUserTitleRole & {
  isSelected: boolean;
};

class Store {
  app: AppStore;
  unregistered: UnregisteredStore;
  registered: RegisteredStore;

  constructor(props: InitialProps) {
    this.app = new AppStore(this, props);
    this.unregistered = new UnregisteredStore(this);
    this.registered = new RegisteredStore(this);
  }
}

class StoreBase {
  protected parent: Store;

  constructor(parent: Store) {
    this.parent = parent;
  }
}

class AppStore extends StoreBase {
  readonly titleUuid: string;

  constructor(parent: Store, props: InitialProps) {
    super(parent);
    this.titleUuid = props.titleUuid;
  }
}

class UnregisteredStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor isCreating = false;
  @observable accessor alertShow = false;
  @observable accessor userTitleRoles: UserTitleRole[] = [];
  @observable accessor ndids = "";
  @observable accessor role = Role.owner;
  @observable accessor loginidFilter = "";

  shouldShowAlert() {
    return (
      (this.role === Role.owner ||
        this.role === Role.developer ||
        this.role === Role.business) &&
      (this.notNclNdids().length > 0 || this.notNclUsers().length > 0)
    );
  }

  notNclUsers() {
    return this.userTitleRoles.filter((_) => _.isSelected && !_.isNcl);
  }

  notNclNdids() {
    if (this.ndids === "") {
      return [];
    }
    const loginidToIsNcl: { [index: string]: boolean } = {};
    for (const userTitleRole of this.userTitleRoles) {
      loginidToIsNcl[userTitleRole.loginid.toLowerCase()] = userTitleRole.isNcl;
    }
    return this.ndids.split(",").filter((ndid) => {
      const lowerNdid = ndid.toLowerCase();
      return lowerNdid in loginidToIsNcl ? !loginidToIsNcl[lowerNdid] : true;
    });
  }

  @action async listUserTitleRoles() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const response = await call(
      Api.unregisteredUserTitleRole(this.parent.app.titleUuid, {
        loginidFilter: this.loginidFilter,
      }),
    );
    if (response.code != 200) {
      return;
    }
    this.userTitleRoles = response.body.map((role) => ({
      ...role,
      isSelected: false,
    }));
  }

  @action selectUsers(loginids: string[], isSelected: boolean) {
    const loginIdsSet = new Set(loginids);
    for (const userTitleRole of this.userTitleRoles) {
      if (loginIdsSet.has(userTitleRole.loginid)) {
        userTitleRole.isSelected = isSelected;
      }
    }
  }

  @action async createUserTitleRole() {
    this.isCreating = true;
    using _ = defer(() => (this.isCreating = false));
    const response = await call(
      Api.createUserTitleRole(this.parent.app.titleUuid, {
        user: {
          ndids: this.ndids,
          role: this.role,
          uids: this.userTitleRoles
            .filter((_) => _.isSelected)
            .map((_) => _.uid),
        },
      }),
    );
    if (response.code != 200) {
      return;
    }
    notificationStore.notifySuccess(response.body.message);
    this.listUserTitleRoles();
    this.parent.registered.listUserTitleRoles();
  }
}

class RegisteredStore extends StoreBase {
  @observable accessor isLoading = false;
  @observable accessor isModifying = false;
  @observable accessor alertShow = false;
  @observable accessor userTitleRoles: UserTitleRole[] = [];
  @observable accessor role = Role.owner;

  shouldShowAlert() {
    return (
      (this.role === Role.owner ||
        this.role === Role.developer ||
        this.role === Role.business) &&
      this.notNclUsers().length > 0
    );
  }

  notNclUsers() {
    return this.userTitleRoles.filter(
      (userTitleRole) => userTitleRole.isSelected && !userTitleRole.isNcl,
    );
  }

  @action async listUserTitleRoles() {
    this.isLoading = true;
    using _ = defer(() => (this.isLoading = false));
    const response = await call(
      Api.registeredUserTitleRole(this.parent.app.titleUuid),
    );
    if (response.code != 200) {
      return;
    }
    this.userTitleRoles = response.body.map((role) => ({
      ...role,
      isSelected: false,
    }));
  }

  @action selectUsers(loginids: string[], isSelected: boolean) {
    const loginIdsSet = new Set(loginids);
    for (const userTitleRole of this.userTitleRoles) {
      if (loginIdsSet.has(userTitleRole.loginid)) {
        userTitleRole.isSelected = isSelected;
      }
    }
  }

  @action async updateUserTitleRole() {
    this.isModifying = true;
    using _ = defer(() => (this.isModifying = false));
    const response = await call(
      Api.updateUserTitleRole(this.parent.app.titleUuid, {
        user: {
          role: this.role,
          ids: this.userTitleRoles.filter((_) => _.isSelected).map((_) => _.id),
        },
      }),
    );
    if (response.code != 200) {
      return;
    }
    notificationStore.notifySuccess(response.body.message);
    this.listUserTitleRoles();
  }

  @action async deleteUserTitleRole() {
    this.isModifying = true;
    using _ = defer(() => (this.isModifying = false));
    const response = await call(
      Api.destroyUserTitleRole(this.parent.app.titleUuid, {
        user: {
          ids: this.userTitleRoles.filter((_) => _.isSelected).map((_) => _.id),
        },
      }),
    );
    if (response.code != 200) {
      return;
    }
    notificationStore.notifySuccess(response.body.message);
    this.listUserTitleRoles();
    this.parent.unregistered.listUserTitleRoles();
  }
}
